import {
  Box,
  DropdownSelectMultiple,
  HelperField,
  ModalBody,
  ModalFoot,
  ModalHead,
  Radio,
  TagSelectItem,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import * as React from 'react';
import { v4 as uuid } from 'uuid';

import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { FlowManagerEditModalComponentT, FlowType } from 'fable/flowEditor';
import { AutomationNodeTypes, withSelection, withTags } from 'hocs';
import type { WithSelectionInjectedProps, WithTagsInjectedProps } from 'hocs';
import { trackFlowActionSaved } from 'utils/flowEditor/tracking';

import type {
  AutomationNodeTagPayloadT,
  AutomationNodeTagT,
  AutomationTagT,
} from '../nodeTypes';
import { TagOperation } from '../nodeTypes/types';

import styles from './shared.module.scss';

type PropsT = FlowManagerEditModalComponentT<AutomationNodeTagT> &
  WithTagsInjectedProps &
  WithSelectionInjectedProps<AutomationTagT>;

type ValidationT = {
  tags?: string;
};

type StateT = {
  validation: ValidationT;
  payload: AutomationNodeTagPayloadT;
};

class TagModalImp extends React.Component<PropsT, StateT> {
  constructor(props: PropsT) {
    super(props);

    const { node } = props;
    this.state = {
      validation: {},
      payload:
        node.nodeType === AutomationNodeTypes.TAG
          ? node.payload
          : {
              operation: TagOperation.TAG,
              tags: [],
              nextNodeId: uuid(),
            },
    };
  }

  componentDidMount() {
    const { selection } = this.props;
    const { payload } = this.state;
    if (payload.tags.length > 0) {
      selection.setSelectedItems(payload.tags);
    }
  }

  handleAnalytics(): void {
    const { node } = this.props;
    const { payload } = this.state;

    trackFlowActionSaved(!!node.isAction, {
      flowType: FlowType.FLOW_TYPE_AUTOMATION,
      nodeType: AutomationNodeTypes.WAIT,
      settings: {
        operation: payload.operation,
      },
    });
  }

  @boundMethod
  handleConfirm(): void {
    const errors = this.validate();
    if (Object.keys(errors).length > 0) {
      this.setState({ validation: errors });
      return;
    }

    this.saveNode();
  }

  saveNode() {
    const { replaceNode, node, selection } = this.props;
    const { payload } = this.state;

    this.handleAnalytics();

    const nodeOptions: AutomationNodeTagT = {
      ...node,
      nodeType: AutomationNodeTypes.TAG,
      isAction: false,
      isRemovable: true,
      isEditable: true,
      payload: {
        ...payload,
        tags: selection.selectedItems,
      },
    };

    replaceNode(nodeOptions);
    this.closeModal();
  }

  @boundMethod
  closeModal() {
    this.props.triggerClose();
  }

  @boundMethod
  radioOnChange(evt: React.ChangeEvent<HTMLInputElement>) {
    const option = evt.target.id as string;
    const { payload } = this.state;

    this.setState({
      payload: {
        ...payload,
        operation: option === 'tag' ? TagOperation.TAG : TagOperation.UNTAG,
      },
    });
  }

  @boundMethod
  onTagSearch(searchTerm: string | undefined) {
    const { tags } = this.props;
    if (searchTerm !== undefined && searchTerm !== tags.searchTerm) {
      tags.searchTermChange(searchTerm);
    }
  }

  @boundMethod
  onTagSelect(tag: AutomationTagT) {
    const { selection } = this.props;
    selection.selectItem(tag);

    this.setState({
      validation: {},
    });
  }

  validate() {
    const errors: ValidationT = {};
    const { selection } = this.props;
    if (selection.selectedItems.length === 0) {
      errors.tags = 'Please select at least 1 tag';
    }

    return errors;
  }

  getItems() {
    const { tags } = this.props;
    return tags.items.map(
      (tag) =>
        ({
          id: tag.id as string,
          name: tag.name,
          color: tag.color,
        } as AutomationTagT),
    );
  }

  getPagination() {
    const { tags } = this.props;
    return {
      nextEnable: tags.hasNextPage,
      onNext: tags.nextPage,
      previousEnable: tags.hasPreviousPage,
      onPrevious: tags.previousPage,
    };
  }

  @boundMethod
  onToggle(isOpen: boolean, isFirstOpen?: boolean | undefined) {
    const { tags } = this.props;
    if (isOpen && isFirstOpen) {
      tags.getItems();
    }
  }

  render() {
    const { payload, validation } = this.state;
    const { node, nodeLabel, selection } = this.props;

    const isChanged =
      payload.operation !== node.payload?.operation ||
      selection.selectedItems.length !== node.payload?.tags?.length ||
      node.payload?.tags?.some(
        ({ id }, index) => id !== selection.selectedItems[index]?.id,
      );

    return (
      <Box width="600px">
        <ModalHead title={nodeLabel} onClose={this.closeModal}>
          Select the tags you&apos;d like to add or remove from a conversation.
        </ModalHead>
        <ModalBody>
          <Box>
            <Box>
              <label className={styles.optionLabel}>
                <Radio
                  id="tag"
                  name="operation"
                  checked={payload.operation === TagOperation.TAG}
                  onChange={this.radioOnChange as () => void}
                />
                Tag
              </label>
              <label className={styles.optionLabel}>
                <Radio
                  id="untag"
                  name="operation"
                  checked={payload.operation === TagOperation.UNTAG}
                  onChange={this.radioOnChange as () => void}
                />
                Remove tag
              </label>

              <HelperField
                fieldId="tag"
                label="Select tag"
                errorMessage={validation.tags}
                required
              >
                <></>
              </HelperField>
              <DropdownSelectMultiple
                buttonText="+Select tags"
                containerProps={{ className: styles.dropdownContainer }}
                itemComponent={TagSelectItem}
                items={this.getItems()}
                selectedItems={selection.selectedItems}
                pagination={this.getPagination()}
                hasSearch
                onSearch={this.onTagSearch}
                onSelect={this.onTagSelect}
                onRemove={selection.deselectItem}
                onToggle={this.onToggle}
              />
            </Box>
          </Box>
        </ModalBody>
        <ModalFoot
          confirmOnClick={this.handleConfirm}
          confirmName={getConfirmButtonVerb(node)}
          confirmProps={{ disabled: !isChanged }}
          cancelOnClick={this.closeModal}
        />
      </Box>
    );
  }
}

export const TagModal = withTags(
  withSelection<PropsT, AutomationTagT>(TagModalImp, 'id'),
);
